import { isApiError } from "#lib/http/ApiError.ts";

const RETRY_DELAYS_MS = [100, 500, 1_500] as const;

function isRetryable(error: unknown) {
    if (!isApiError(error) || error.status === undefined) {
        return true;
    }

    return error.status < 400 || error.status >= 500 || error.status === 408 || error.status === 429;
}

function delay(ms: number, signal?: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
            reject(signal.reason);

            return;
        }

        const timeoutId = setTimeout(() => {
            signal?.removeEventListener("abort", handleAbort);
            resolve();
        }, ms);

        function handleAbort() {
            clearTimeout(timeoutId);
            reject(signal?.reason);
        }

        signal?.addEventListener("abort", handleAbort, { once: true });
    });
}

export async function fetchSpeechTokenWithRetry<T>(fetchToken: (signal?: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<T> {
    for (const retryDelayMs of RETRY_DELAYS_MS) {
        try {
            return await fetchToken(signal);
        } catch (error) {
            if (signal?.aborted || !isRetryable(error)) {
                throw error;
            }

            try {
                await delay(retryDelayMs, signal);
            } catch {
                throw error;
            }
        }
    }

    return await fetchToken(signal);
}
